import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import moment from 'moment';

import bodyScrollLock from '@elab/core/utils/scroll-lock';
import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/store-locator/index';
import { selectors as uiSelectors } from '@elab/core/store/ui';
import Breakpoints from '@elab/core/utils/Breakpoints';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import { actions as storeLocatorActions } from '@elab/core/store/store-locator';
import { deliveryModes } from '@elab/core/utils/constants';
import {
    COMPONENT_CONNECT,
    COMPONENT_DISCONNECT,
    createDispatcher,
} from '@elab/core/utils/dynaReduxHelpers';
import {
    actions as deliveryActions,
    reducers as deliveryReducers,
    storeName as deliveryStoreName,
    sagaFactory,
} from '@elab/components/connected/DeliveryModes/redux';
import {
    actions as rtsActions,
    reducers as rtsReducers,
    sagaFactory as rtsSagaFactory,
    selectors as rtsSelectors,
    storeName as rtsStoreName,
} from '@elab/components/connected/real-time-stock/redux';
import { actions as storeActions } from '@elab/core/store/store-details';
import * as storage from '@elab/core/utils/storage';
import { button } from '@elab/core/utils/htmlTemplates';

export class DisplayStores extends SimpleLitElement {
    static get properties() {
        return {
            stores: {
                type: Array,
            },
            apiParameters: { type: Object },
            titleOpenHours: {
                type: String,
                attribute: 'title-open-hours',
            },
            orderCode: {
                type: String,
                attribute: 'order-code',
            },
            titleServices: { type: String, attribute: 'title-services' },
            getStockFrom: { type: String, attribute: 'stock-from' },
            quantityLabel: { type: String, attribute: 'quantity-label' },
            fullOrderLabel: { type: String, attribute: 'full-order-label' },
            storeClosingTime: { type: String, attribute: 'store-closing-time' },
            incompleteOrderLabel: { type: String, attribute: 'incomplete-order-label' },
            storeOpenLabel: { type: String, attribute: 'store-open-label' },
            storeClosingLabel: { type: String, attribute: 'store-closing-label' },
            storeClosedLabel: { type: String, attribute: 'store-closed-label' },
            closedLabel: {
                type: String,
                attribute: 'closed-hours',
            },
            viewStoreInfoLabel: {
                type: String,
                attribute: 'view-store-info-label',
            },
            ccSelectStoreLabel: { type: String, attribute: 'cc-select-store-label' },
            pickupSelectStoreLabel: { type: String, attribute: 'pickup-select-store-label' },
            storePopupTitleLabel: {
                type: String,
                attribute: 'store-popup-title-label',
            },
            storePopupTitleLabelCE: {
                type: String,
                attribute: 'store-popup-title-label-ce',
            },
            stockInformationLabel: { type: String, attribute: 'stock-information-label' },
            noStockInformationLabel: { type: String, attribute: 'no-stock-information-label' },
            storeInfoPopupOpened: { type: Boolean, attribute: false },
            currentStoreInfo: { type: Object, attribute: false },
            googleApiKey: { type: String, attribute: 'google-api-key' },
            googleClientId: { type: String, attribute: 'google-client-id' },
            googleApiVersion: { type: String, attribute: 'google-api-version' },
            markerImagePath: { type: String, attribute: 'marker-image-path' },
            markerColor: { type: String, attribute: 'marker-color' },
            isGoogleMapsApiLoaded: { type: Boolean, attribute: false },
            mapStyles: { type: Array, attribute: 'map-styles' },
            defaultZoom: { type: Number, attribute: 'default-zoom' },
            dynamic: { type: Boolean },
            displayStores: { type: Boolean, attribute: 'display-stores' },
            windowInnerWidth: { type: Number, attribute: false },
            emptyResultText: { type: String, attribute: 'empty-result-text' },
            nonAvailableLabel: { type: String, attribute: 'not-available-label' },
            availableLabel: { type: String, attribute: 'available-label' },
            emptyQueryErrorLabel: {
                type: String,
                attribute: 'empty-query-error-label',
            },
            selectedStore: { type: String, attribute: false },
            cartGuid: { type: String },
            selectedDeliveryMode: { type: String, attribute: false },
            queryRequired: { type: Boolean, attribute: false },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
            deliveryMode: { type: Object, attribute: false },
            closeLabel: {
                type: String,
                attribute: 'close-label',
            },
            customMarkerLabel: { type: String, attribute: 'custom-marker-label' },
            redirectLink: { type: String, attribute: 'redirect-link' },
        };
    }

    get isClickExpress() {
        return this.getStockFrom === 'productRTS';
    }

    constructor() {
        super();

        this.stores = [];
        this.apiParameters = {};
        this.titleOpenHours = '';
        this.titleServices = '';
        this.closedLabel = '';
        this.viewStoreInfoLabel = '';
        this.ccSelectStoreLabel = '';
        this.pickupSelectStoreLabel = '';
        this.selectStoreLabel = '';
        this.storeInfoPopupOpened = false;
        this.currentStoreInfo = null;
        this.emptyResultText = '';
        this.selectedStore = '';
        this.cartGuid = '';
        this.analyticsConfig = '';
        this.getStockFrom = '';
        this.deliveryMode = {};
        this.isPostPoint = false;
        this.customMarkerLabel = '';
        this.windowInnerWidth = window.innerWidth;
    }

    afterConnect() {
        this.dispatchEvent(new CustomEvent(COMPONENT_CONNECT));
        this.dispatchEvent(new CustomEvent('clear-store-details'));
    }

    beforeDisconnect() {
        this.dispatchEvent(new CustomEvent(COMPONENT_DISCONNECT));
    }

    updated(changedProperties) {
        if (this.deliveryMode && this.deliveryMode.type !== '') {
            this.selectStoreLabel =
                this.deliveryMode.type === 'POST_POINT'
                    ? this.pickupSelectStoreLabel
                    : this.ccSelectStoreLabel;
            this.isPostPoint = this.deliveryMode.type === 'POST_POINT';
        }
        if (
            changedProperties.has('stores') ||
            changedProperties.has('selectedDeliveryMode') ||
            changedProperties.has('cartGuid')
        ) {
            this.selectStore();
        }
        if (changedProperties.has('storeInfoPopupOpened')) {
            if (this.storeInfoPopupOpened) {
                bodyScrollLock.enable();
            } else {
                bodyScrollLock.disable();
            }
        }
    }

    selectStore(store) {
        const storeToSelect = !store
            ? this.stores && this.stores.length
                ? this.stores[0]
                : {}
            : store;
        const autoMode = !store && storeToSelect === this.stores[0];
        const storeName = !storeToSelect.name ? '' : storeToSelect.name;
        const geoPoint = !storeToSelect.geoPoint ? {} : storeToSelect.geoPoint;
        if (storeName && this.selectedDeliveryMode && this.cartGuid) {
            this.selectedStore = storeName;

            const detailToChange = {
                deliveryModeId: this.selectedDeliveryMode,
                forceToRedirect: !autoMode,
                redirectLink: this.redirectLink,
            };

            if (storeName) {
                detailToChange.storeId = storeName;
                const storageDistance = {
                    cart: this.cartGuid,
                    store: storeName,
                    distance: storeToSelect.distanceKm ? storeToSelect.distanceKm : '',
                };
                if (this.deliveryMode && this.deliveryMode.type === deliveryModes.POST_POINT) {
                    this.dispatchEvent(
                        new CustomEvent('select-store', {
                            detail: storeName,
                        }),
                    );
                }
                const lastUsedStore = {
                    displayName: storeName,
                    ...geoPoint,
                };
                if (store) {
                    lastUsedStore.geoPoint = store && { ...store.geoPoint };
                }
                storage.set('lastUsedStore', JSON.stringify(lastUsedStore));
                storage.set('pickup-store-distance', storageDistance, true);
            }

            this.dispatchEvent(
                new CustomEvent('change-store', {
                    detail: detailToChange,
                }),
            );
        }
    }

    togglePopupStatus(store) {
        this.currentStoreInfo = store;
        this.storeInfoPopupOpened = !this.storeInfoPopupOpened;
        if (this.orderCode) this.dispatchEvent(new CustomEvent('close-store-info'));
    }

    getStoreDetails = (store, showPopupLink, renderMap) => {
        return html`
            <div class="store-locator">
                <div class="store-locator__store-name">
                    <i class="icon icon-home"></i>
                    ${store.distanceKm
                        ? html`
                              <span class="store-locator__store-distance">
                                  <span class="store-locator__store-distance--value">
                                      ${store.distanceKm.toString().replace('.', ',')}
                                  </span>
                                  <span class="store-locator__store-distance--measure">
                                      km
                                  </span>
                              </span>
                          `
                        : html``}
                    ${store.name}
                </div>
                <div class="address-card">
                    <div class="address-card__address">
                        <div>
                            <span class="address-card__address--town">${store.address.town}</span>
                            ${store.address && store.address.district
                                ? html`
                                      <span class="address-card__address--district">
                                          ${store.address.district}
                                      </span>
                                  `
                                : null}
                            <span class="address-card__address--line1">${store.address.line1}</span>
                        </div>
                        <span class="address-card__address--line2">
                            ${store.address.postalCode}
                        </span>
                    </div>
                </div>

                <div class="phone-card">
                    <div class="phone-card__number">
                        ${store.address.phone}
                    </div>
                </div>
                ${this.renderRtsSection(store, showPopupLink)}
                ${showPopupLink
                    ? html`
                          <div class="store-locator__view">
                              <span @click="${() => this.togglePopupStatus(store)}"
                                  >${this.viewStoreInfoLabel}</span
                              >
                          </div>
                      `
                    : ''}
            </div>
            ${renderMap
                ? html`
                      <div class="store-locator-map">
                          <e2-dynamic-map
                              marker-image-path="${this.markerImagePath}"
                              google-api-key="${this.googleApiKey}"
                              google-client-id="${this.googleClientId}"
                              google-api-version="${this.googleApiVersion}"
                              default-zoom="${this.defaultZoom}"
                              .point="${store.geoPoint}"
                              .mapStyles="${this.mapStyles}"
                              custom-label="${this.customMarkerLabel}"
                              ?dynamic="${this.dynamic}"
                              marker-color="${this.markerColor}"
                          ></e2-dynamic-map>
                      </div>
                  `
                : ''}
            ${this.isClickExpress && !this.storeInfoPopupOpened
                ? html`
                      <div class="rts-store rts-store__services-popup-button">
                          <e2-click-tracking
                              class="rts-store__services-popup-tracking"
                              analytics-config="${this.analyticsConfig}"
                              type="pick_up-store_selection"
                              @click=${() => this.selectStore(store)}
                          >
                              <e2-action-button class-modifier="button--primary" action-type="">
                                  ${this.selectStoreLabel}
                              </e2-action-button>
                          </e2-click-tracking>
                      </div>
                  `
                : html``}
        `;
    };

    getPopupTemplate() {
        const classes = classMap({
            'popup-container--opened': this.storeInfoPopupOpened,
        });
        const headerClass = `popup-container__title ${
            this.isClickExpress ? 'popup-container__title--cce' : ''
        }`;
        return html`
            <div class="popup-container ${classes}">
                <div class="popup-container__header">
                    <div class="${headerClass}">
                        <i class="icon icon-home"></i>
                        ${this.isClickExpress
                            ? this.storePopupTitleLabelCE
                            : this.storePopupTitleLabel}
                    </div>
                    <span
                        class="popup-container__toggle icon icon-close"
                        @click="${() => this.togglePopupStatus(null)}"
                    ></span>
                </div>
                ${this.getStoreDetails(this.currentStoreInfo, false, true)}
                ${this.displayStores && this.getPickupButtonTemplate(this.currentStoreInfo)
                    ? this.getPickupButtonTemplate(this.currentStoreInfo)
                    : html`
                          <div class="close-popup-button">
                              <div class="close-button-container">
                                  <e2-button
                                      class-modifier="button--primary
                    "
                                      full-width
                                      button-label="${this.closeLabel}"
                                      @click="${() => this.togglePopupStatus(null)}"
                                  ></e2-button>
                              </div>
                          </div>
                      `}
                ${this.getHoursTableTemplate(
                    this.currentStoreInfo.openingHours.weekDayOpeningList,
                    this.currentStoreInfo,
                )}
                ${this.isClickExpress
                    ? this.getRtsServicesTemplate(this.currentStoreInfo)
                    : this.getServicesTemplate(this.currentStoreInfo)}
            </div>
        `;
    }

    getHoursTableTemplate(weekDayOpeningList, store) {
        return !(this.isClickExpress && !this.storeInfoPopupOpened)
            ? html`
                  <div class="near-store__hours ${this.isPostPoint ? 'postpoint' : ''}">
                      <div class="near-store__hours-title">
                          ${this.titleOpenHours}
                      </div>
                      <e2-hours-table
                          .closedLabel="${this.closedLabel}"
                          .openHours="${weekDayOpeningList}"
                      ></e2-hours-table>
                      ${this.isPostPoint ? this.getPickupButtonTemplate(store) : ''}
                  </div>
              `
            : html`
                  ${this.getRtsHoursTableTemplate(weekDayOpeningList, store)}
              `;
    }

    getRtsHoursTableTemplate(weekDayOpeningList, store) {
        return html`
            <div class="rts-store__column">
                <div class="rts-store__row">
                    <div class="rts-store__hours">
                        <div class="rts-store__hours-title">
                            ${this.titleOpenHours}
                        </div>
                        <e2-hours-table
                            .closedLabel="${this.closedLabel}"
                            .openHours="${weekDayOpeningList}"
                        ></e2-hours-table>
                    </div>
                    ${this.getRtsServicesTemplate(store)}
                </div>
                ${this.getPickupButtonTemplate(store, 'rts-store__services-button')}
            </div>
        `;
    }

    getCategoryLabel(item) {
        const variantCategory = item.entry.variantValueCategories
            ? item.entry.variantValueCategories[0]
            : null;
        if (variantCategory) {
            const superCategories = variantCategory.superCategories[0];
            if (superCategories) {
                return `${superCategories.code}-${variantCategory.name}`;
            }
        }
        return '';
    }

    getStockDetails(store) {
        let inStock = true;
        let count = 0;
        const notInStock = [];
        store.entriesWithStock.forEach(item => {
            if (item.entry.quantity > item.stock.stockLevel) {
                count += item.entry.quantity - item.stock.stockLevel;
            }
            inStock = inStock && item.entry.quantity <= item.stock.stockLevel;
            if (item.entry.quantity > item.stock.stockLevel) {
                notInStock.push({
                    brandName: item.entry.product.masterBrand
                        ? item.entry.product.masterBrand.name
                        : '',
                    productName: item.entry.product.name,
                    remainingQty: item.entry.quantity - item.stock.stockLevel,
                    qty: item.entry.quantity,
                    stockLevel: item.stock.stockLevel,
                    category: this.getCategoryLabel(item),
                });
            }
        });

        return { inStock, count, notInStock };
    }

    getStockInformation(store) {
        return this.isClickExpress
            ? html`
                  <div class="rts-store__stock-info">
                      ${this.getStockDetails(store).inStock
                          ? html`
                                <div class="rts-store__stock-info rts-store__stock-info--ca">
                                    <i class="icon icon-check-mark rts-store__in-stock-icon"></i>
                                    <div class="rts-store__in-stock">
                                        ${this.stockInformationLabel}
                                    </div>
                                </div>
                            `
                          : html`
                                <div class="rts-store__not-in-stock">
                                    <div class="rts-store__not-in-stock-title">
                                        <span class="rts-store__not-in-stock-icon"></span>
                                        ${this.noStockInformationLabel.replace(
                                            '{0}',
                                            this.getStockDetails(store).count,
                                        )}
                                    </div>
                                    ${this.getStockDetails(store).notInStock.map(
                                        item => html`
                                            <div class="rts-store__item">
                                                <div class="rts-store__item-title">
                                                    ${item.brandName}
                                                </div>
                                                <div class="rts-store__item-name">
                                                    ${item.productName}
                                                </div>
                                                <div class="rts-store__item-description">
                                                    ${item.category}
                                                </div>
                                                <div class="rts-store__item-quantity">
                                                    <span class="rts-store__item-key"
                                                        >${this.quantityLabel} :
                                                    </span>
                                                    <div class="rts-store__item-value">
                                                        <span
                                                            class="rts-store__item-not-available"
                                                            ?hidden="${item.remainingQty < 0}"
                                                        >
                                                            ${this.nonAvailableLabel.replace(
                                                                '{0}',
                                                                Math.abs(item.remainingQty),
                                                            )}
                                                        </span>
                                                        <span
                                                            class="rts-store__item-available"
                                                            ?hidden="${item.stockLevel == 0}"
                                                        >
                                                            ${this.availableLabel.replace(
                                                                '{0}',
                                                                item.stockLevel,
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        `,
                                    )}
                                </div>
                            `}
                  </div>
              `
            : html``;
    }

    getServicesTemplate(store) {
        const { features = {} } = store;
        return !(this.isClickExpress && !this.storeInfoPopupOpened)
            ? html`
                  <div class="near-store__services">
                      <div class="near-store__services-title">
                          ${this.titleServices}
                      </div>
                      ${Object.keys(features).length && features.entry
                          ? html`
                                <div class="near-store__services-list">
                                    ${features.entry.map(
                                        idx =>
                                            html`
                                                <p>${idx.value}</p>
                                            `,
                                    )}
                                </div>
                            `
                          : ''}
                      ${this.getPickupButtonTemplate(store)} ${this.getPickupButtonTemplate(store)}
                  </div>
              `
            : html``;
    }

    getRtsServicesTemplate(store) {
        const { features = {} } = store;
        return html`
            <div class="rts-store__services">
                <div class="rts-store__services-title">
                    ${this.titleServices}
                </div>
                ${Object.keys(features).length && features.entry
                    ? html`
                          <div class="rts-store__services-list">
                              ${features.entry.map(
                                  service => html`
                                      <span class="rts-store__service"
                                          >${service.value ? service.value : service.key}</span
                                      >
                                  `,
                              )}
                          </div>
                      `
                    : ''}
            </div>
        `;
    }

    getPickupButtonTemplate(store, styleName = 'near-store__services-button') {
        const classModifier =
            this.selectedStore === store.name ? 'button--primary' : 'button--invert';

        return html`
            <div class="${styleName}">
                <e2-click-tracking
                    analytics-config="${this.analyticsConfig}"
                    type="pick_up-store_selection"
                >
                    ${button(
                        {
                            className: classModifier,
                            clickHandler: () => this.selectStore(store),
                        },
                        this.selectStoreLabel,
                    )}
                </e2-click-tracking>
            </div>
        `;
    }

    getListOfStoresTemplate() {
        const storesToDisplay = this.stores.slice(0, this.apiParameters.pageSize || 10);
        const classes = classMap({
            'display-stores--opened': this.storeInfoPopupOpened,
        });
        return html`
            <div class="display-stores ${classes}">
                ${this.currentStoreInfo && this.storeInfoPopupOpened ? this.getPopupTemplate() : ''}
                ${this.displayStores &&
                    storesToDisplay.map(
                        store => html`
                            <div class="cards__wrapper">
                                <div
                                    class="near-store
                                ${this.selectedStore === store.name ? 'near-store__selected' : ''}"
                                >
                                    <div
                                        class="near-store__info ${this.isPostPoint
                                            ? 'postpoint'
                                            : ''}"
                                    >
                                        ${this.getStoreDetails(
                                            store,
                                            true,
                                            !Breakpoints.isMobileOnly(this.windowInnerWidth),
                                        )}
                                        ${!this.isClickExpress
                                            ? this.getPickupButtonTemplate(store)
                                            : null}
                                    </div>
                                    ${this.getHoursTableTemplate(
                                        store.openingHours.weekDayOpeningList,
                                        store,
                                    )}
                                    ${this.getStockInformation(store)}
                                    ${this.isPostPoint ? '' : this.getServicesTemplate(store)}
                                </div>
                            </div>
                        `,
                    )}
            </div>
        `;
    }

    setErrorLabel = () => {
        if (this.queryRequired) {
            return this.emptyQueryErrorLabel;
        }
        if (Object.keys(this.apiParameters).length) {
            return this.emptyResultText;
        }
    };

    getStoreWorkingStatus({ openingHours: { weekDayOpeningList } }) {
        const { closed, openingTime, closingTime, formattedHour } = weekDayOpeningList[
            moment().isoWeekday() - 1
        ];
        const { storeOpenLabel, storeClosedLabel, storeClosingLabel } = this;

        if (closed) {
            return { statusLabel: storeClosedLabel, styleName: 'close' };
        }

        const currentHour = moment().hour();
        const openingDiff = openingTime.hour - currentHour;
        const closingDiff = closingTime.hour - currentHour;

        let statusLabel = storeOpenLabel;
        let styleName = 'open';

        if (closingDiff < 0) {
            statusLabel = storeClosedLabel;
            styleName = 'close';
        } else if (openingDiff > closingDiff) {
            statusLabel = storeClosingLabel.replace('{0}', formattedHour);
            styleName = 'closing';
        }

        return { statusLabel, styleName };
    }

    isOrderFull(store) {
        return this.getStockDetails(store).inStock;
    }

    renderRtsSection(store, showPopupLink) {
        const workingStatus = this.getStoreWorkingStatus(store);
        const classStyle = `rts-store__information--${workingStatus.styleName}`;
        return this.isClickExpress && showPopupLink
            ? html`
                  <div class="rts-store__information">
                      ${this.isOrderFull(store)
                          ? html`
                                <div class="rts-store__full-order">
                                    <i class="icon icon-check"></i>
                                    ${this.fullOrderLabel}
                                </div>
                            `
                          : html`
                                <div class="rts-store__incomplete-order">
                                    <i class="icon icon-close"></i>
                                    ${this.incompleteOrderLabel}
                                </div>
                                ${this.getStockDetails(store).notInStock.map(
                                    item => html`
                                        <div
                                            class="rts-store__item rts-store__mobile-store-details"
                                        >
                                            <div class="rts-store__item-title">
                                                ${item.brandName}
                                            </div>
                                            <div class="rts-store__item-name">
                                                ${item.productName}
                                            </div>
                                            <div class="rts-store__item-description">
                                                ${item.category}
                                            </div>
                                            <div class="rts-store__item-quantity">
                                                <span class="rts-store__item-key"
                                                    >${this.quantityLabel} :
                                                </span>
                                                <div class="rts-store__item-value">
                                                    <span
                                                        class="rts-store__item-not-available"
                                                        ?hidden="${item.remainingQty < 0}"
                                                    >
                                                        ${this.nonAvailableLabel.replace(
                                                            '{0}',
                                                            Math.abs(item.remainingQty),
                                                        )}
                                                    </span>
                                                    <span
                                                        class="rts-store__item-available"
                                                        ?hidden="${item.stockLevel == 0}"
                                                    >
                                                        ${this.availableLabel.replace(
                                                            '{0}',
                                                            item.stockLevel,
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    `,
                                )}
                            `}
                  </div>
                  <div class="${classStyle}">
                      ${workingStatus.statusLabel}
                  </div>
              `
            : html``;
    }

    render() {
        return html`
            ${this.stores && this.stores.length
                ? this.getListOfStoresTemplate()
                : html`
                      ${(this.queryRequired || Object.keys(this.apiParameters).length) &&
                      !this.isrtsError
                          ? html`
                                <e2-message type="error" class="display-stores__message">
                                    <div v-slot="message">
                                        ${this.setErrorLabel()}
                                    </div>
                                </e2-message>
                            `
                          : html``}
                  `}
        `;
    }
}

const mapStateToProps = (state, ctx) => ({
    stores:
        ctx.getStockFrom !== 'productRTS'
            ? selectors.getStores(state)
            : rtsSelectors.getCartStores(state),
    apiParameters: selectors.getApiParameters(state),
    windowInnerWidth: uiSelectors.getWindowSize(state).width,
    selectedDeliveryMode: cartSelectors.getDeliveryModeCode(state) || '',
    cartGuid: cartSelectors.getGuid(state) || '',
    queryRequired: selectors.getQueryRequiredValue(state),
    deliveryMode: cartSelectors.getDeliveryMode(state),
    isrtsError: rtsSelectors.getCartStoresErrorType(state) || '',
});

const mapDispatchToEvents = (dispatch, ctx) => {
    return ctx.getStockFrom !== 'productRTS'
        ? createDispatcher(
              {
                  deliveryStoreName,
                  deliveryReducers,
                  sagaFactory,
                  initAction: deliveryActions.initialize(),
                  additionalEvents: {
                      'change-store': ({ detail }) =>
                          dispatch(deliveryActions.setDeliveryMode(detail)),

                      'clear-store-details': () => dispatch(storeActions.setStoreDetails({})),
                      'select-store': e =>
                          dispatch(storeLocatorActions.selectStore({ storeId: e.detail })),
                  },
              },
              dispatch,
          )
        : {
              ...createDispatcher(
                  {
                      storeName: rtsStoreName,
                      reducers: rtsReducers,
                      sagaFactory: rtsSagaFactory,
                      initAction: rtsActions.initialize(),
                  },
                  dispatch,
              ),
              ...createDispatcher(
                  {
                      deliveryStoreName,
                      deliveryReducers,
                      sagaFactory,
                      initAction: deliveryActions.initialize(),
                      additionalEvents: {
                          'change-store': ({ detail }) =>
                              dispatch(deliveryActions.setDeliveryMode(detail)),

                          'clear-store-details': () => dispatch(storeActions.setStoreDetails({})),
                      },
                  },
                  dispatch,
              ),
          };
};

export default () =>
    customElements.define(
        'e2-display-stores',
        connect(mapStateToProps, mapDispatchToEvents)(DisplayStores),
    );
